import React from "react"
import { useApp } from "../context/AppContext"
import { Icon } from "../shared"
import {
  getAppraisalByToken,
  saveAppraisalByToken,
  submitAppraisalByToken,
} from "../api/appraisals.api"

/**
 * Layar yang dibuka atasan dari tautan di emailnya.
 *
 * Satu butir per layar, bukan satu halaman panjang — meniru formulir Typeform
 * yang sudah dipakai hari ini. Alasannya bukan gaya: seorang manajer yang
 * dikirimi satu halaman berisi dua puluh baris isian akan menutupnya, sedangkan
 * satu pertanyaan besar di layar kosong terasa seperti pekerjaan tiga menit.
 *
 * Empat hal yang menentukan apakah ia benar-benar terasa seperti itu:
 *
 *  • KEYBOARD. Angka memilih skala, Enter maju, Esc mundur. Sekali seseorang
 *    menemukannya, sisa formulir diisi tanpa menyentuh tetikus sama sekali.
 *  • SIMPAN BERTAHAP. Tiap maju satu layar, jawabannya sudah di server.
 *    Kehilangan isian karena tab tertutup adalah cara tercepat membuat seorang
 *    atasan tidak pernah kembali.
 *  • MUNDUR SELALU BOLEH. Ini formulir kepegawaian, bukan kuis.
 *  • RINGKASAN SEBELUM KIRIM. Typeform tidak punya ini; di sini ia perlu,
 *    karena yang ditandatangani seseorang adalah dokumen tentang orang lain.
 */

// ─── Tampilan ────────────────────────────────────────────────────────────────

const wrap = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  background: "var(--bg, #f8fafc)",
  fontFamily: "var(--font-sans, system-ui, sans-serif)",
}

const panggung = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 20px 64px",
}

const kartu = { width: "100%", maxWidth: 680 }

function fmtTanggal(v) {
  if (!v) return null
  return new Date(v).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })
}

/** Bilah kemajuan setipis mungkin — penanda, bukan hiasan. */
function Kemajuan({ nilai }) {
  return (
    <div style={{ position: "sticky", top: 0, height: 3, background: "var(--border, #e2e8f0)", zIndex: 5 }}>
      <div
        style={{
          height: "100%",
          width: `${Math.round(nilai * 100)}%`,
          background: "var(--program-color, #0EA5E9)",
          transition: "width .35s ease",
        }}
      />
    </div>
  )
}

function Kepala({ issuer }) {
  return (
    <div className="row gap-3" style={{ padding: "14px 20px", alignItems: "center" }}>
      {issuer?.logoUrl
        ? <img src={issuer.logoUrl} alt="" style={{ height: 28 }} />
        : null}
      <span className="small" style={{ fontWeight: 600 }}>{issuer?.name || "Penilaian Kinerja"}</span>
    </div>
  )
}

// ─── Layar ───────────────────────────────────────────────────────────────────

export default function AppraisalReviewScreen() {
  // Token dibaca dari pageState, bukan dari prop — pola yang sama dengan
  // VerifyPublicScreen dan PublicCollectionScreen, yang juga masuk lewat
  // parameter query dan dirender tanpa cangkang aplikasi.
  const { pageState } = useApp()
  const token = pageState?.token

  const [data, setData] = React.useState(null)
  const [galat, setGalat] = React.useState("")
  const [memuat, setMemuat] = React.useState(true)

  // -1 = sampul, 0..n-1 = butir, n = ringkasan, n+1 = terima kasih
  const [langkah, setLangkah] = React.useState(-1)
  const [jawaban, setJawaban] = React.useState({})
  const [catatan, setCatatan] = React.useState("")
  const [tersimpan, setTersimpan] = React.useState(false)
  const [mengirim, setMengirim] = React.useState(false)
  const [arah, setArah] = React.useState(1)

  // Animasi penuh layar adalah pemicu nyata bagi sebagian orang. Dihormati,
  // bukan sekadar diperkecil.
  const kurangiGerak = React.useMemo(
    () => typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  )

  React.useEffect(() => {
    let batal = false
    getAppraisalByToken(token)
      .then((d) => {
        if (batal) return
        setData(d)
        const awal = {}
        for (const a of d.answers || []) awal[a.itemId] = { score: a.score, text: a.text, note: a.note }
        setJawaban(awal)
        setCatatan(d.overallNote || "")
        if (d.status !== "PENDING") setLangkah(-2) // sudah dikirim
      })
      .catch((e) => { if (!batal) setGalat(e?.response?.data?.error || e.message) })
      .finally(() => { if (!batal) setMemuat(false) })
    return () => { batal = true }
  }, [token])

  const butir = data?.form?.items || []
  const total = butir.length
  const sekarang = langkah >= 0 && langkah < total ? butir[langkah] : null

  const warna = data?.issuer?.primaryColor
  React.useEffect(() => {
    if (warna) document.documentElement.style.setProperty("--program-color", warna)
  }, [warna])

  // Simpan satu butir ke server. Tidak ditunggu penampilnya: layar berikutnya
  // muncul seketika, dan indikator "tersimpan" menyusul.
  const simpan = React.useCallback((itemId) => {
    const isi = jawaban[itemId]
    if (!isi) return
    saveAppraisalByToken(token, {
      answers: [{ item_id: itemId, score: isi.score ?? null, text: isi.text ?? null, note: isi.note ?? null }],
    })
      .then(() => { setTersimpan(true); setTimeout(() => setTersimpan(false), 1600) })
      .catch(() => { /* pengiriman akhir tetap membawa seluruh jawaban */ })
  }, [jawaban, token])

  const maju = React.useCallback(() => {
    if (sekarang) simpan(sekarang.id)
    setArah(1)
    setLangkah((l) => Math.min(l + 1, total))
  }, [sekarang, simpan, total])

  const mundur = React.useCallback(() => {
    setArah(-1)
    setLangkah((l) => Math.max(l - 1, -1))
  }, [])

  const isiButir = (itemId, patch) =>
    setJawaban((j) => ({ ...j, [itemId]: { ...(j[itemId] || {}), ...patch } }))

  // ── Papan ketik ────────────────────────────────────────────────────────────
  React.useEffect(() => {
    function onKey(e) {
      if (langkah <= -2 || langkah > total) return

      if (e.key === "Escape") { e.preventDefault(); mundur(); return }

      if (e.key === "Enter" && !e.shiftKey) {
        // Shift+Enter membuat baris baru di esai; Enter polos maju.
        if (e.target?.tagName === "TEXTAREA" && e.shiftKey) return
        e.preventDefault()
        if (langkah === -1) { setArah(1); setLangkah(0); return }
        if (langkah < total) { maju(); return }
        return
      }

      // Angka memilih jawaban, di butir berskala maupun berpilihan. Hanya bila
      // fokusnya tidak sedang di dalam kolom teks.
      const diKolomTeks = e.target?.tagName === "TEXTAREA" || e.target?.tagName === "INPUT"
      if (diKolomTeks) return

      const angka = Number(e.key)
      if (!Number.isInteger(angka) || angka < 1) return

      if (sekarang?.type === "SCALE" && angka <= (data?.form?.scaleMax || 5)) {
        e.preventDefault()
        isiButir(sekarang.id, { score: angka })
      } else if (sekarang?.type === "CHOICE") {
        // Nomor URUTAN pilihan, bukan skornya. Pilihan pertama selalu ditekan
        // dengan "1" meski skornya 5 — yang dilihat penilai di layar adalah
        // nomor barisnya.
        const opsi = sekarang.options || []
        if (angka <= opsi.length) {
          e.preventDefault()
          isiButir(sekarang.id, { score: Number(opsi[angka - 1]?.score) })
        }
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [langkah, total, sekarang, maju, mundur, data])

  async function kirim() {
    setMengirim(true)
    setGalat("")
    try {
      await submitAppraisalByToken(token, {
        answers: butir.map((i) => ({
          item_id: i.id,
          score: jawaban[i.id]?.score ?? null,
          text: jawaban[i.id]?.text ?? null,
          note: jawaban[i.id]?.note ?? null,
        })),
        overall_note: catatan || null,
      })
      setLangkah(total + 1)
    } catch (e) {
      setGalat(e?.response?.data?.error || e.message)
    } finally {
      setMengirim(false)
    }
  }

  // ── Keadaan khusus ─────────────────────────────────────────────────────────

  if (memuat) return <Pesan judul="Memuat…" />
  if (galat && !data) return <Pesan judul="Tautan tidak berlaku" isi={galat} />
  if (langkah === -2) {
    return <Pesan judul="Penilaian ini sudah dikirim" isi="Terima kasih — tidak ada lagi yang perlu Anda lakukan." ikon="check" />
  }
  if (langkah === total + 1) {
    return (
      <Pesan
        judul="Terima kasih."
        isi={`Penilaian untuk ${data.subject.name} sudah terkirim ke ${data.issuer?.name || "penyelenggara"}.`}
        ikon="check"
      />
    )
  }

  const kemajuan = langkah < 0 ? 0 : Math.min((langkah + 1) / (total + 1), 1)
  const transisi = kurangiGerak
    ? {}
    : { animation: `masuk-${arah > 0 ? "maju" : "mundur"} .28s ease` }

  return (
    <div style={wrap}>
      <style>{`
        @keyframes masuk-maju  { from { opacity: 0; transform: translateY(14px) } to { opacity: 1; transform: none } }
        @keyframes masuk-mundur{ from { opacity: 0; transform: translateY(-14px) } to { opacity: 1; transform: none } }
        @media (prefers-reduced-motion: reduce) {
          [data-panggung] { animation: none !important }
        }
      `}</style>

      <Kemajuan nilai={kemajuan} />
      <Kepala issuer={data.issuer} />

      <div style={panggung}>
        <div style={{ ...kartu, ...transisi }} data-panggung key={langkah}>
          {langkah === -1 && <Sampul data={data} onMulai={() => { setArah(1); setLangkah(0) }} />}

          {sekarang && (
            <Butir
              item={sekarang}
              nomor={langkah + 1}
              total={total}
              scaleMax={data.form.scaleMax}
              scaleLabels={data.form.scaleLabels}
              nilai={jawaban[sekarang.id] || {}}
              onUbah={(patch) => isiButir(sekarang.id, patch)}
              onMaju={maju}
              onMundur={mundur}
            />
          )}

          {langkah === total && (
            <Ringkasan
              data={data}
              jawaban={jawaban}
              catatan={catatan}
              onCatatan={setCatatan}
              onLompat={(i) => { setArah(-1); setLangkah(i) }}
              onMundur={mundur}
              onKirim={kirim}
              mengirim={mengirim}
              galat={galat}
            />
          )}
        </div>
      </div>

      <div style={{ textAlign: "center", padding: "0 20px 28px", minHeight: 22 }}>
        <span className="tiny muted" style={{ opacity: tersimpan ? 1 : 0, transition: "opacity .3s" }}>
          <Icon name="check" size={10} /> Tersimpan
        </span>
      </div>
    </div>
  )
}

// ─── Bagian-bagiannya ────────────────────────────────────────────────────────

function Pesan({ judul, isi, ikon }) {
  return (
    <div style={{ ...wrap, alignItems: "center", justifyContent: "center", textAlign: "center", padding: 24 }}>
      <div style={{ maxWidth: 460 }}>
        {ikon && (
          <div style={{
            width: 52, height: 52, borderRadius: "50%", margin: "0 auto 18px",
            background: "var(--green-10, #dcfce7)", display: "grid", placeItems: "center",
          }}>
            <Icon name={ikon} size={24} color="var(--green-60, #16a34a)" />
          </div>
        )}
        <h1 className="h1" style={{ fontSize: 26, margin: 0 }}>{judul}</h1>
        {isi && <p className="muted mt-2" style={{ lineHeight: 1.6 }}>{isi}</p>}
      </div>
    </div>
  )
}

function Sampul({ data, onMulai }) {
  const s = data.subject
  const baris = [
    ["Jabatan", s.position],
    ["Unit kerja", s.department],
    ["Awal kontrak", fmtTanggal(s.contractStartAt)],
    ["Habis kontrak", fmtTanggal(s.contractEndAt)],
    ["Periode", data.periodLabel],
  ].filter(([, v]) => v)

  return (
    <div>
      <div className="tiny" style={{ textTransform: "uppercase", letterSpacing: ".12em", color: "var(--text-3)" }}>
        {data.issuer?.name}
      </div>
      <h1 className="h1" style={{ fontSize: 30, margin: "8px 0 4px", letterSpacing: "-.01em" }}>
        {data.form.name}
      </h1>

      <div style={{
        marginTop: 26, padding: "22px 24px", borderRadius: 14,
        background: "var(--surface, #fff)", border: "1px solid var(--border, #e2e8f0)",
      }}>
        <div style={{ fontSize: 24, fontWeight: 500 }}>{s.name}</div>
        {baris.length > 0 && (
          <dl style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 14, margin: "18px 0 0",
          }}>
            {baris.map(([k, v]) => (
              <div key={k}>
                <dt className="tiny muted" style={{ textTransform: "uppercase", letterSpacing: ".07em" }}>{k}</dt>
                <dd style={{ margin: "3px 0 0", fontSize: 14, fontWeight: 500 }}>{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {data.form.intro && (
        <p className="muted mt-4" style={{ lineHeight: 1.65 }}>{data.form.intro}</p>
      )}

      <div className="row gap-3 mt-4" style={{ alignItems: "center" }}>
        <button className="btn-primary" onClick={onMulai} style={tombolBesar}>
          Mulai menilai
        </button>
        <span className="small muted">
          Sekitar {data.form.estimatedMinutes} menit · {data.form.items.length} pertanyaan
        </span>
      </div>
      <div className="tiny muted mt-3">
        Tekan <Kbd>Enter</Kbd> untuk mulai. Jawaban tersimpan otomatis, jadi Anda bisa berhenti dan melanjutkan kapan saja.
      </div>
    </div>
  )
}

function Butir({ item, nomor, total, scaleMax, scaleLabels, nilai, onUbah, onMaju, onMundur }) {
  const areaRef = React.useRef(null)
  React.useEffect(() => { if (item.type === "ESSAY") areaRef.current?.focus() }, [item])

  const bisaMaju = item.type === "ESSAY" ? true : nilai.score != null

  return (
    <div>
      <div className="tiny muted" style={{ letterSpacing: ".08em" }}>
        {nomor} dari {total}
      </div>
      <h2 style={{ fontSize: 26, fontWeight: 500, margin: "10px 0 0", lineHeight: 1.3, letterSpacing: "-.01em" }}>
        {item.label}
      </h2>
      {item.helpText && <p className="muted mt-2" style={{ lineHeight: 1.6 }}>{item.helpText}</p>}

      <div className="mt-4">
        {item.type === "SCALE" ? (
          <Skala
            max={scaleMax}
            labels={scaleLabels}
            nilai={nilai.score}
            onPilih={(n) => onUbah({ score: n })}
          />
        ) : item.type === "CHOICE" ? (
          <Pilihan
            opsi={item.options || []}
            nilai={nilai.score}
            onPilih={(skor) => onUbah({ score: skor })}
          />
        ) : (
          <textarea
            ref={areaRef}
            value={nilai.text || ""}
            onChange={(e) => onUbah({ text: e.target.value })}
            rows={5}
            placeholder="Tulis jawaban Anda…"
            style={{
              width: "100%", padding: "14px 16px", fontSize: 16, lineHeight: 1.6,
              borderRadius: 10, border: "1px solid var(--border, #e2e8f0)",
              background: "var(--surface, #fff)", fontFamily: "inherit", resize: "vertical",
            }}
          />
        )}
      </div>

      {item.type !== "ESSAY" && (
        <textarea
          value={nilai.note || ""}
          onChange={(e) => onUbah({ note: e.target.value })}
          rows={2}
          placeholder="Catatan (opsional)"
          style={{
            width: "100%", marginTop: 14, padding: "10px 14px", fontSize: 14,
            borderRadius: 10, border: "1px solid var(--border, #e2e8f0)",
            background: "transparent", fontFamily: "inherit", resize: "vertical",
          }}
        />
      )}

      <div className="row gap-2 mt-4" style={{ alignItems: "center" }}>
        <button className="btn secondary" onClick={onMundur} style={tombolKecil}>Kembali</button>
        <button className="btn-primary" onClick={onMaju} disabled={!bisaMaju} style={{ ...tombolBesar, opacity: bisaMaju ? 1 : .5 }}>
          {nomor === total ? "Lihat ringkasan" : "Berikutnya"}
        </button>
        <span className="tiny muted">
          {item.type === "SCALE"
            ? <>Tekan <Kbd>1</Kbd>–<Kbd>{scaleMax}</Kbd> lalu <Kbd>Enter</Kbd></>
            : item.type === "CHOICE"
              ? <>Tekan <Kbd>1</Kbd>–<Kbd>{(item.options || []).length}</Kbd> lalu <Kbd>Enter</Kbd></>
              : <>Tekan <Kbd>Enter</Kbd> untuk lanjut, <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> untuk baris baru</>}
        </span>
      </div>
    </div>
  )
}

/**
 * Daftar pilihan berkalimat penuh.
 *
 * Menumpuk ke bawah, bukan berjajar seperti skala: tiap pilihan di sini adalah
 * satu kalimat utuh yang menggambarkan tingkat perilaku ("Tidak pernah absen
 * dan selalu tepat waktu"), dan kalimat sepanjang itu mustahil dibaca dalam
 * lima kolom sempit.
 *
 * Nomor di kiri adalah nomor URUTAN, bukan skornya. Penilai menekan "1" untuk
 * pilihan teratas meski skornya 5 — angka yang dilihatnya di layar harus sama
 * dengan angka yang ditekannya.
 */
function Pilihan({ opsi, nilai, onPilih }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {opsi.map((o, i) => {
        const skor = Number(o?.score)
        const aktif = nilai === skor
        return (
          <button
            key={i}
            type="button"
            onClick={() => onPilih(skor)}
            aria-pressed={aktif}
            style={{
              display: "flex", gap: 12, alignItems: "flex-start", textAlign: "left",
              padding: "13px 16px", borderRadius: 11, cursor: "pointer",
              border: `1.5px solid ${aktif ? "var(--program-color, #0EA5E9)" : "var(--border, #e2e8f0)"}`,
              background: aktif ? "var(--program-color, #0EA5E9)" : "var(--surface, #fff)",
              color: aktif ? "#fff" : "inherit",
              fontFamily: "inherit", fontSize: 14, lineHeight: 1.5, transition: "all .12s",
            }}
          >
            <span
              className="mono"
              style={{
                flexShrink: 0, width: 22, height: 22, borderRadius: 6, fontSize: 12,
                display: "grid", placeItems: "center", marginTop: 1,
                border: `1px solid ${aktif ? "rgba(255,255,255,.5)" : "var(--border, #e2e8f0)"}`,
                opacity: aktif ? 1 : .7,
              }}
            >{i + 1}</span>
            <span>{o?.label}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Tombol skala berlabel.
 *
 * Angka telanjang membuat dua penilai mengartikan "3" secara berbeda, dan
 * selisih tafsir itu tidak pernah muncul di laporan — ia hanya membuat angkanya
 * tidak bisa dibandingkan. Karena itu labelnya selalu ikut, bukan hanya di
 * ujung-ujungnya.
 */
function Skala({ max, labels, nilai, onPilih }) {
  const daftar = Array.from({ length: max }, (_, i) => i + 1)
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(110px, 1fr))`, gap: 10 }}>
      {daftar.map((n) => {
        const aktif = nilai === n
        return (
          <button
            key={n}
            type="button"
            onClick={() => onPilih(n)}
            aria-pressed={aktif}
            style={{
              padding: "14px 10px", borderRadius: 12, cursor: "pointer",
              border: `1.5px solid ${aktif ? "var(--program-color, #0EA5E9)" : "var(--border, #e2e8f0)"}`,
              background: aktif ? "var(--program-color, #0EA5E9)" : "var(--surface, #fff)",
              color: aktif ? "#fff" : "inherit",
              fontFamily: "inherit", textAlign: "center", transition: "all .12s",
            }}
          >
            <div className="mono" style={{ fontSize: 20, fontWeight: 600 }}>{n}</div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: aktif ? .95 : .7 }}>
              {labels?.[n - 1] || ""}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function Ringkasan({ data, jawaban, catatan, onCatatan, onLompat, onMundur, onKirim, mengirim, galat }) {
  const butir = data.form.items
  const belum = butir.filter((i) => i.type !== "ESSAY" && jawaban[i.id]?.score == null)

  return (
    <div>
      <h2 style={{ fontSize: 26, fontWeight: 500, margin: 0 }}>Periksa sebelum mengirim</h2>
      <p className="muted mt-2" style={{ lineHeight: 1.6 }}>
        Setelah dikirim, penilaian ini tidak bisa diubah lagi.
      </p>

      <div className="mt-4" style={{
        border: "1px solid var(--border, #e2e8f0)", borderRadius: 12, overflow: "hidden",
        background: "var(--surface, #fff)",
      }}>
        {butir.map((i, idx) => {
          const isi = jawaban[i.id] || {}
          const kosong = i.type === "ESSAY" ? !isi.text : isi.score == null
          return (
            <button
              key={i.id}
              type="button"
              onClick={() => onLompat(idx)}
              style={{
                display: "block", width: "100%", textAlign: "left", cursor: "pointer",
                padding: "13px 16px", background: "transparent", fontFamily: "inherit",
                border: "none", borderTop: idx > 0 ? "1px solid var(--border, #e2e8f0)" : "none",
              }}
            >
              <div className="tiny muted">{i.label}</div>
              <div className="small" style={{ marginTop: 3, fontWeight: 500, color: kosong ? "var(--amber-60, #b45309)" : "inherit" }}>
                {i.type === "ESSAY"
                  ? (isi.text || "Belum diisi")
                  : i.type === "CHOICE"
                    ? ((i.options || []).find((o) => Number(o?.score) === isi.score)?.label || "Belum dipilih")
                    : (isi.score != null
                      ? `${isi.score} — ${data.form.scaleLabels?.[isi.score - 1] || ""}`
                      : "Belum dinilai")}
              </div>
            </button>
          )
        })}
      </div>

      <textarea
        value={catatan}
        onChange={(e) => onCatatan(e.target.value)}
        rows={3}
        placeholder="Catatan keseluruhan (opsional)"
        style={{
          width: "100%", marginTop: 16, padding: "12px 14px", fontSize: 15,
          borderRadius: 10, border: "1px solid var(--border, #e2e8f0)",
          background: "var(--surface, #fff)", fontFamily: "inherit", resize: "vertical",
        }}
      />

      {belum.length > 0 && (
        <div className="small mt-3" style={{ color: "var(--amber-60, #b45309)" }}>
          {belum.length} butir belum dinilai. Butir yang dilewati tidak dihitung ke nilai akhir —
          ia tidak dianggap nol.
        </div>
      )}
      {galat && <div className="small mt-3" style={{ color: "var(--red-60, #dc2626)" }}>{galat}</div>}

      <div className="row gap-2 mt-4">
        <button className="btn secondary" onClick={onMundur} style={tombolKecil}>Kembali</button>
        <button className="btn-primary" onClick={onKirim} disabled={mengirim} style={tombolBesar}>
          {mengirim ? "Mengirim…" : "Kirim penilaian"}
        </button>
      </div>
    </div>
  )
}

function Kbd({ children }) {
  return (
    <kbd style={{
      fontFamily: "var(--font-mono, monospace)", fontSize: 11, padding: "1px 5px",
      border: "1px solid var(--border, #e2e8f0)", borderRadius: 4, background: "var(--surface, #fff)",
    }}>{children}</kbd>
  )
}

const tombolBesar = {
  padding: "12px 24px", fontSize: 15, fontWeight: 600, borderRadius: 10,
  border: "1px solid transparent", background: "var(--program-color, #0EA5E9)",
  color: "#fff", cursor: "pointer", fontFamily: "inherit",
}

const tombolKecil = {
  padding: "12px 18px", fontSize: 14, fontWeight: 500, borderRadius: 10,
  border: "1px solid var(--border, #e2e8f0)", background: "transparent",
  cursor: "pointer", fontFamily: "inherit",
}
